/**
 * apps/web :: entitlement :: RequiresEntitlement
 *
 * Route guard — wraps routes that REQUIRE a non-expired entitlement
 * (`/training`, `/practice`, `/exam/*`, `/rule-diff/*`). When the
 * authenticated + onboarded user has zero active entitlements:
 *
 *   - Redirect to `/dashboard`. The dashboard renders the
 *     `<TrialExpiredPaywall>` body in that state, so the user lands on
 *     the conversion surface rather than a 404 or a paywall page they
 *     can't navigate away from.
 *
 * Loading: render the same lightweight status placeholder used by
 * `<ProtectedRoute>` and `<OnboardingGate>`, so the chrome doesn't
 * flash between gates while the entitlement probe is in flight.
 *
 * IMPORTANT: this guard does NOT replace `<PaidSurfaceGate>`. The two
 * coexist:
 *   - `<RequiresEntitlement>` is the post-trial expired-vs-active gate
 *     for the broader nav surface.
 *   - `<PaidSurfaceGate>` is the per-tier per-state gate that decides
 *     whether the user has access to a SPECIFIC paid product slice
 *     (`tier="cdl"`, `state="TX"`, etc.).
 *
 * Mount order: ProtectedRoute → OnboardingGate → EntitlementGateProvider
 * → RequiresEntitlement → PaidSurfaceGate → page.
 */
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";

import { useEntitlement } from "@/entitlement/EntitlementGateContext";

export interface RequiresEntitlementProps {
  children: ReactNode;
}

export default function RequiresEntitlement({
  children,
}: RequiresEntitlementProps): JSX.Element {
  const { t } = useTranslation();
  const { isLoading, isExpired } = useEntitlement();

  if (isLoading) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="requires-entitlement-loading"
        className="mx-auto mt-xl max-w-md p-md text-center text-neutral-700"
      >
        {t("paywall.loading", { defaultValue: "Checking your access…" })}
      </div>
    );
  }

  if (isExpired) {
    // Bounce to the dashboard — DashboardPage swaps its body for the
    // TrialExpiredPaywall when isExpired === true. Using `replace` so
    // the back button doesn't bounce the user back into a guard loop.
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
